/** Take over / hand back an alias (plan backend `2026-10-06-managed-ext-ids.md`): who
 *  keeps it — an import (`managed: true`, updated on every sync, read-only to ops) or
 *  ops (`managed: false`, never touched by an import).
 *
 *  One confirmation, one PATCH of the flag alone, for the grid's row menu and the
 *  detail drawer. Both directions are a change of owner rather than of the mapping, so
 *  the dialog says what the import will and will not do next (`ownership.ts`); handing
 *  back is the risky one (the next sync overwrites hand edits, and deletes the ID once
 *  the feed stops listing it — for an ID with no integration, the next XML import does).
 *  A refusal — the server's `{managed: "..."}` or anything else —
 *  is the snackbar's, and the rows are refetched either way: the server's state is
 *  what the page must show next. */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import * as extIdsApi from '@/api/extIds';
import type { ExtIdListItem } from '@/api/extIds';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useSnackbar } from '@/components/Snackbar';

import { messageOf } from './extIdErrors';
import { managedSource } from './extIdFormat';
import { TAKE_OVER_EFFECT, handBackEffect } from './ownership';

/** The parts of a row the ownership change reads. */
export type OwnershipRow = Pick<
  ExtIdListItem,
  'id' | 'kind' | 'value' | 'managed' | 'integration' | 'restech_account'
>;

function dialogText(row: OwnershipRow) {
  if (row.managed) {
    return {
      title: 'Take over this external ID?',
      message:
        `“${row.value}” is maintained by an import ` +
        `(${managedSource(row.integration, row.restech_account)}). Taking it over makes it ` +
        `yours: ${TAKE_OVER_EFFECT}. You can then edit or delete it here.`,
      confirmLabel: 'Take over',
      destructive: false,
    };
  }
  return {
    title: 'Hand this external ID back to the import?',
    message:
      `${handBackEffect(row.integration, row.restech_account, `“${row.value}”`)}. ` +
      'It is read-only here until you take it over again.',
    confirmLabel: 'Hand back',
    destructive: true,
  };
}

export function useExtIdOwnership(): {
  /** Ask to flip the row's owner (opens the confirmation). */
  request: (row: OwnershipRow) => void;
  /** The confirmation — render it once, beside the caller's other dialogs (in a
   *  Drawer: a sibling of its content Box, never inside it). */
  dialog: ReactNode;
  isPending: boolean;
} {
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const [row, setRow] = useState<OwnershipRow | null>(null);

  const mutation = useMutation({
    mutationFn: (r: OwnershipRow) => extIdsApi.update(r.id, { managed: !r.managed }),
    onSuccess: (_data, r) => {
      snackbar.show(
        r.managed
          ? 'Taken over — imports no longer update this external ID.'
          : 'Handed back — the import maintains this external ID from its next sync.',
        'success',
      );
    },
    onError: (e) => {
      snackbar.show(messageOf(e, 'Could not change who maintains this external ID.'), 'error');
    },
    onSettled: () => {
      setRow(null);
      // `['ext-ids']` also covers the drawer's detail query.
      void qc.invalidateQueries({ queryKey: ['ext-ids'] });
    },
  });

  const text = row ? dialogText(row) : null;
  const dialog = (
    <ConfirmDialog
      open={row !== null}
      title={text?.title ?? ''}
      message={text?.message ?? ''}
      confirmLabel={text?.confirmLabel ?? 'Confirm'}
      destructive={text?.destructive ?? false}
      onConfirm={() => {
        if (row && !mutation.isPending) mutation.mutate(row);
      }}
      onCancel={() => setRow(null)}
    />
  );

  return { request: setRow, dialog, isPending: mutation.isPending };
}
